import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useCustodySchedules, useCustodyExceptions } from '../hooks/useCustody'
import { ScheduleWizard } from './ScheduleWizard'
import { ExceptionModal } from './ExceptionModal'
import { SkeletonList } from '../../../components/ui/Skeleton'
import { cn } from '../../../lib/utils'

const SCHEDULE_TYPE_LABELS = {
  alternating_weeks:  'Alternating Weeks',
  two_two_three:      '2-2-3 Rotation',
  two_two_five_five:  '2-2-5-5 Rotation',
  every_weekend:      'Every Weekend',
  custom:             'Custom',
}

const REASON_LABELS = {
  holiday:      'Holiday',
  school_event: 'School Event',
  vacation:     'Vacation',
  other:        'Other',
}

const ExceptionsTab = ({ scheduleId, isAdmin }) => {
  const { data: exceptions = [], isLoading } = useCustodyExceptions(scheduleId)
  const { remove } = useCustodyExceptions(scheduleId)
  const today = new Date()

  const future = exceptions.filter((e) => new Date(e.exception_date) >= today)
  const past   = exceptions.filter((e) => new Date(e.exception_date) < today)

  if (isLoading) return <div className="py-4 text-center text-sm text-text-muted">Loading…</div>
  if (exceptions.length === 0) return <p className="text-sm text-text-muted py-4 text-center">No exceptions yet.</p>

  return (
    <div className="space-y-4">
      {future.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Upcoming</p>
          <div className="space-y-1">
            {future.map((ex) => (
              <div key={ex.id} className="flex items-center justify-between bg-background rounded-xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-text-primary">
                    {format(parseISO(ex.exception_date), 'MMM d, yyyy')}
                  </p>
                  <p className="text-xs text-text-muted">{REASON_LABELS[ex.reason] ?? ex.reason}</p>
                  {ex.notes && <p className="text-xs text-text-muted">{ex.notes}</p>}
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(ex.id)}
                    className="text-rose-500 text-xs font-medium ml-3"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {past.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Past</p>
          <div className="space-y-1 opacity-60">
            {past.slice(0, 5).map((ex) => (
              <div key={ex.id} className="bg-background rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-text-primary">
                  {format(parseISO(ex.exception_date), 'MMM d, yyyy')}
                </p>
                <p className="text-xs text-text-muted">{REASON_LABELS[ex.reason] ?? ex.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const ScheduleCard = ({ schedule, isAdmin, onDeactivate, onDelete }) => {
  const [expanded, setExpanded]     = useState(false)
  const [showAddEx, setShowAddEx]   = useState(false)
  const [activeTab, setActiveTab]   = useState('info')
  const [confirmDel, setConfirmDel] = useState(false)

  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 text-left"
      >
        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: schedule.color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary">{schedule.schedule_name}</p>
          <p className="text-xs text-text-muted mt-0.5">
            {schedule.child?.display_name} · {SCHEDULE_TYPE_LABELS[schedule.schedule_type]}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!schedule.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-border text-text-muted">Inactive</span>
          )}
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            className={cn('text-text-muted transition-transform', expanded ? 'rotate-180' : '')}
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border">
          {/* Tabs */}
          <div className="flex border-b border-border">
            {['info', 'exceptions'].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex-1 py-2 text-xs font-semibold capitalize transition-colors',
                  activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-text-muted',
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="px-4 py-4">
            {activeTab === 'info' && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-muted">Type</span>
                  <span className="text-text-primary font-medium">{SCHEDULE_TYPE_LABELS[schedule.schedule_type]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Start date</span>
                  <span className="text-text-primary font-medium">{format(parseISO(schedule.start_date), 'MMM d, yyyy')}</span>
                </div>
                {schedule.end_date && (
                  <div className="flex justify-between">
                    <span className="text-text-muted">End date</span>
                    <span className="text-text-primary font-medium">{format(parseISO(schedule.end_date), 'MMM d, yyyy')}</span>
                  </div>
                )}
                {schedule.notes && <p className="text-text-secondary text-xs mt-2">{schedule.notes}</p>}

                {isAdmin && (
                  <div className="flex gap-2 pt-3">
                    <button
                      type="button"
                      onClick={() => setShowAddEx(true)}
                      className="flex-1 border border-border rounded-xl py-2 text-xs font-medium text-text-secondary"
                    >
                      Add Exception
                    </button>
                    {schedule.is_active && (
                      <button
                        type="button"
                        onClick={() => onDeactivate(schedule.id)}
                        className="border border-border rounded-xl py-2 px-3 text-xs font-medium text-text-secondary"
                      >
                        Deactivate
                      </button>
                    )}
                    {confirmDel ? (
                      <>
                        <button type="button" onClick={() => setConfirmDel(false)}
                          className="flex-1 border border-border rounded-xl py-2 text-xs font-medium text-text-secondary">
                          Cancel
                        </button>
                        <button type="button" onClick={() => onDelete(schedule.id)}
                          className="flex-1 bg-rose-600 text-white rounded-xl py-2 text-xs font-semibold">
                          Confirm
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setConfirmDel(true)}
                        className="border border-border rounded-xl py-2 px-3 text-xs font-medium text-rose-500">
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'exceptions' && (
              <ExceptionsTab scheduleId={schedule.id} isAdmin={isAdmin} />
            )}
          </div>
        </div>
      )}

      {showAddEx && (
        <ExceptionModal
          schedule={schedule}
          prefillDate={null}
          originalHouseholdId={null}
          onClose={() => setShowAddEx(false)}
        />
      )}
    </div>
  )
}

export const ScheduleList = () => {
  const { isAdmin }               = useAuth()
  const { data: schedules = [], isLoading, update, remove } = useCustodySchedules()
  const [showWizard, setShowWizard] = useState(false)

  const handleDeactivate = (id) => update.mutate({ id, is_active: false })
  const handleDelete     = (id) => remove.mutate(id)

  if (isLoading) return <div className="px-4 py-5"><SkeletonList count={2} /></div>

  return (
    <div className="space-y-3">
      {schedules.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-8">No schedules yet.</p>
      ) : (
        schedules.map((s) => (
          <ScheduleCard
            key={s.id}
            schedule={s}
            isAdmin={isAdmin}
            onDeactivate={handleDeactivate}
            onDelete={handleDelete}
          />
        ))
      )}

      {isAdmin && (
        <button
          type="button"
          onClick={() => setShowWizard(true)}
          className="w-full border border-dashed border-border rounded-xl py-3 text-sm font-medium text-text-secondary hover:border-primary hover:text-primary transition-colors"
        >
          + New Schedule
        </button>
      )}

      {showWizard && <ScheduleWizard onClose={() => setShowWizard(false)} />}
    </div>
  )
}
